import { useCallback, useMemo } from 'react';
import type { TaiKhoanNganHang, TaiKhoanNganHangInput } from '@/types/schemas';
import { ConfirmationDialog, ErpDataTableV2 } from '@/components/custom/erp';
import { getBankAccountColumns } from './cols-definition';
import { useForms, useTaiKhoanNganHang } from '@/hooks';
import { ActionBar, FormDialog } from './components';

export default function DanhMucTaiKhoanNganHangPage() {
  const {
    taiKhoanNganHangs,
    isLoading,
    addTaiKhoanNganHang,
    updateTaiKhoanNganHang,
    deleteTaiKhoanNganHang,
    refreshTaiKhoanNganHangs
  } = useTaiKhoanNganHang();
  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<TaiKhoanNganHang>();

  const handleOpenViewForRow = useCallback(
    (row: TaiKhoanNganHang) => {
      handleRowClick({ id: row.uuid, row });
      handleViewClick();
    },
    [handleRowClick, handleViewClick]
  );

  const handleSubmit = useCallback(
    async (data: TaiKhoanNganHangInput) => {
      try {
        if (formMode === 'add') {
          await addTaiKhoanNganHang(data);
        } else if (formMode === 'edit' && selectedObj) {
          await updateTaiKhoanNganHang(selectedObj.uuid, data);
        }

        handleCloseForm();
        clearSelection();
      } catch (error) {
        console.error(error);
      }
    },
    [addTaiKhoanNganHang, clearSelection, formMode, handleCloseForm, selectedObj, updateTaiKhoanNganHang]
  );

  const tables = useMemo(
    () => [
      {
        name: 'Tài khoản ngân hàng',
        rows: taiKhoanNganHangs,
        columns: getBankAccountColumns(handleOpenViewForRow),
        disableColumnFilters: true
      }
    ],
    [handleOpenViewForRow, taiKhoanNganHangs]
  );

  return (
    <div className='flex h-screen flex-col overflow-hidden'>
      {showForm && (
        <FormDialog
          open={showForm}
          onSubmit={handleSubmit}
          formMode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : selectedObj || undefined}
          onClose={handleCloseForm}
          onAdd={() => {
            handleCloseForm();
            clearSelection();
            window.setTimeout(() => {
              handleAddClick();
            }, 100);
          }}
          onEdit={() => {
            if (!selectedObj) return;
            handleCloseForm();
            handleEditClick();
          }}
          onDelete={() => {
            if (!selectedObj) return;
            handleCloseForm();
            handleDeleteClick();
          }}
          onCopy={() => {
            if (!selectedObj) return;
            handleCloseForm();
            handleCopyClick();
          }}
        />
      )}

      {showDelete && selectedObj && (
        <ConfirmationDialog
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={async () => {
            await deleteTaiKhoanNganHang(selectedObj.uuid);
            handleCloseDelete();
            clearSelection();
          }}
          title='Xóa dữ liệu'
          message='Bạn có chắc chắn muốn xóa không?'
        />
      )}

      <ActionBar
        onAddClick={handleAddClick}
        onRefreshClick={refreshTaiKhoanNganHangs}
        onEditClick={() => selectedObj && handleEditClick()}
        onCopyClick={() => selectedObj && handleCopyClick()}
        onDeleteClick={() => selectedObj && handleDeleteClick()}
        onFixedColumnsClick={() => {}}
        onExportClick={() => {}}
        isViewDisabled={!selectedObj}
      />

      <div className='min-h-0 flex-1 overflow-hidden'>
        <ErpDataTableV2
          tables={tables}
          selectedRowId={selectedRowIndex || undefined}
          onRowClick={params =>
            handleRowClick({
              id: String(params.id),
              row: params.row as TaiKhoanNganHang
            })
          }
          getRowId={row => row.uuid}
          totalItems={taiKhoanNganHangs.length}
          currentPage={0}
          pageSize={Math.max(taiKhoanNganHangs.length, 1)}
          loading={isLoading}
          widthMode='stretch'
          className='h-full'
        />
      </div>
    </div>
  );
}
